import { useAsyncEffect } from '@/hooks';
import { downloadBlob } from '@/lib/utils';
import { Column, Form, Grid, Input, Radio, Row, Segment, TextArea } from '@/semantic-ui/components';
import { useStores } from '@/store';
import { Formik, FormikProps } from 'formik';
import { differenceWith, isEqual } from 'lodash';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { useParams } from 'react-router';
import { moment, OnboardingFormProperties } from 'sol-data';
import { array, boolean, mixed, number, object, string } from 'yup';
import Breadcrumbs from '../Breadcrumbs';
import Bulletin from '../Bulletin';
import ButtonBar from '../ButtonBar';
import { CreateOnboardingFormModal } from '../CreateOnboardingFormModal';
import DragDrop from '../DragDrop';
import { PositionGroup } from '../PositionGroup';
import style from './create-onboarding-form-template.less';

const { Field } = Form;

export const MAX_FILE_SIZE = 15000000;
const acceptedFileTypes = {
  'application/pdf': ['.pdf'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
};

export const CreateOnboardingFormTemplate = observer(() => {
  const { documentId } = useParams<{ documentId: string }>();
  const { onboardingFormStore, userStore } = useStores();
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [completedEmployees, setCompletedEmployees] = useState(0);
  const [shouldOpenFinishModal, setShouldOpenFinishModal] = useState(false);
  // Modal state kept for future implementation
  const [_, setShouldOpenPreviewModal] = useState(false);

  useAsyncEffect(async () => {
    if (documentId !== 'add' && documentId !== undefined) {
      const { totalCount, completedCount } = await onboardingFormStore.fetchEmployeeCounts(
        documentId,
      );

      setTotalEmployees(totalCount);
      setCompletedEmployees(completedCount);
    }
  }, []);

  useAsyncEffect(async () => {
    onboardingFormStore.clearCurrent();

    if (documentId && documentId !== 'add') {
      await onboardingFormStore.fetch(documentId);
    }
  }, [documentId, onboardingFormStore]);

  const { errorMessage, isLoading, current: onboardingForm } = onboardingFormStore;

  let initialValues: OnboardingFormProperties & { file: File | undefined } = {
    title: '',
    description: '',
    positions: [],
    exampleFileName: '',
    exampleAzureBlobName: '',
    file: undefined,
    requireSignature: false,
    requireUserUpload: true,
  };

  const { fullName } = userStore.current || { fullName: '' };
  let createdBy = `${fullName} - ${moment().format('YYYY-MM-DD')}`;

  if (onboardingForm) {
    initialValues = {
      ...initialValues,
      title: onboardingForm.title,
      description: onboardingForm.description,
      positions: onboardingForm.positions.map(({ id }) => ({
        id,
      })),
      exampleFileName: onboardingForm.exampleFileName,
      exampleAzureBlobName: onboardingForm.exampleAzureBlobName,
      file: onboardingForm.exampleFileName ? onboardingForm.file ?? undefined : undefined,
      requireSignature: onboardingForm.requireSignature,
      requireUserUpload: onboardingForm.requireUserUpload,
    };
    createdBy = `${onboardingForm.postedBy} - ${onboardingForm.postedOn}`;
  }

  const handlePreviewClick = async (file: File) => {
    downloadBlob(file, file.name);
    setShouldOpenPreviewModal(true);
  };

  const closeModal = () => {
    setShouldOpenFinishModal(false);
  };

  return (
    <>
      <Segment basic padded="very">
        <Breadcrumbs
          linkTitle="Onboarding Forms"
          activeBreadcrumb={onboardingForm?.title ?? 'New Form'}
          link="/onboarding/forms/templates"
          title="Onboarding Forms"
        />
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={object().shape({
            title: string().required('Title is required'),
            description: string().required('Description is required'),
            file: mixed()
              .test(
                'fileSize',
                'File size must be less than 15mb',
                (file?: File | undefined) => !file || file.size === 0 || file.size <= MAX_FILE_SIZE,
              )
              .test(
                'fileType',
                `Unsupported file format. Please select ${Object.values(acceptedFileTypes)
                  .flat()
                  .join(', ')}.`,
                (file?: File) =>
                  !file?.size ||
                  Object.values(acceptedFileTypes)
                    .flat()
                    .includes(`.${file?.name.split('.').pop()}`),
              ),
            exampleFileName: string()
              .when('file', {
                is: (file?: File) => (file?.size ?? 0) > 0,
                then: () => string().required('File name is required'),
                otherwise: () => string(),
              })
              .label('File Name'),
            requireSignature: boolean().required('Signature is required'),
            positions: array()
              .of(
                object().shape({
                  id: number().min(1).required('Position must be selected'),
                }),
              )
              .min(1, 'You must select at least one position'),
          })}
          onSubmit={async (
            {
              file,
              title,
              description,
              exampleFileName,
              positions,
              requireSignature,
              requireUserUpload,
            },
            { setSubmitting },
          ) => {
            if (documentId && documentId !== 'add') {
              if (positions.length < initialValues.positions.length) {
                onboardingFormStore.setError('You cannot remove positions');
              } else {
                let upload: { exampleAzureBlobName: string } | undefined;

                const modifiedPositions = differenceWith(
                  positions,
                  initialValues.positions,
                  isEqual,
                );

                // Files can be uploaded on the edit since the admin might want to add an example file after creating the template
                if (file && file.size > 0) {
                  const readyFormData = new FormData();
                  readyFormData.append('file', file);

                  upload = await onboardingFormStore.uploadOnboardingFormExample(readyFormData);
                }

                await onboardingFormStore.update({
                  id: documentId,
                  positions: modifiedPositions,
                  description,
                  title,
                  requireSignature,
                  requireUserUpload,
                  exampleAzureBlobName: upload?.exampleAzureBlobName,
                  exampleFileName,
                });
              }
            } else {
              let upload: { exampleAzureBlobName: string } | undefined;

              if (file && file.size > 0) {
                const readyFormData = new FormData();
                readyFormData.append('file', file);

                upload = await onboardingFormStore.uploadOnboardingFormExample(readyFormData);
              }

              await onboardingFormStore.create({
                title,
                description,
                exampleAzureBlobName: upload?.exampleAzureBlobName,
                exampleFileName,
                positions,
                requireSignature,
                requireUserUpload,
              });
            }
            setSubmitting(false);
            if (onboardingFormStore.errorMessage === undefined) {
              setShouldOpenFinishModal(true);
            }
          }}
        >
          {({
            handleSubmit,
            values,
            isSubmitting,
            handleChange,
            setFieldValue,
            touched,
            errors,
          }: FormikProps<OnboardingFormProperties & { file: File | undefined }>) => {
            const showTitleError = !!touched.title && !!errors.title;
            const showDescriptionError = !!touched.description && !!errors.description;
            const showPositionsError = !!touched.positions && !!errors.positions;
            const showFileError = !!touched.file && !!errors.file;

            return (
              <Grid>
                <div
                  style={{
                    color: completedEmployees === totalEmployees ? 'green' : 'red',
                  }}
                >
                  {totalEmployees > 0 && `${completedEmployees}/${totalEmployees} completed`}
                </div>
                <Row>
                  <Bulletin
                    errorMessage={errorMessage}
                    handleDismiss={() => onboardingFormStore.clearError()}
                    isLoading={isLoading}
                  />
                </Row>
                <Row>
                  <Column width={8}>
                    <Field>
                      <p className={style.form_headers}>Title</p>
                      <Input
                        name="title"
                        value={values.title}
                        placeholder="Title"
                        onChange={handleChange}
                        fluid
                      />
                      {showTitleError && <p className="error_class">{errors.title}</p>}
                    </Field>
                  </Column>
                  <Column width={8}>
                    <Field>
                      <p className={style.form_headers}>Created By</p>
                      <Input value={createdBy} placeholder={createdBy} readOnly fluid />
                    </Field>
                  </Column>
                </Row>
                <Row>
                  <Column width={8}>
                    <Field>
                      <p className={style.form_headers}>Description</p>
                      <Form>
                        <TextArea
                          name="description"
                          value={values.description}
                          placeholder="Details of document"
                          rows={15}
                          onChange={handleChange}
                          autoHeight
                          className={style.input}
                        />
                      </Form>
                      {showDescriptionError && <p className="error_class">{errors.description}</p>}
                    </Field>
                  </Column>
                  <Column width={8}>
                    <Field>
                      <p className={style.form_headers}>Example File</p>

                      <DragDrop
                        text="Drag and drop files if you want to upload an example"
                        dragText="Drop your file here!"
                        accept={acceptedFileTypes}
                        files={values.file && values.file instanceof File ? [values.file] : []}
                        allowReplace
                        height={285}
                        onFileDrop={(files: File[]) => {
                          if (files?.[0]) {
                            setFieldValue('exampleFileName', files[0].name);
                            setFieldValue('file', files[0]);
                          }
                        }}
                        onFileDelete={() => {
                          setFieldValue('exampleFileName', '');
                          setFieldValue('file', undefined);
                        }}
                      />
                      {showFileError && <p className="error_class">{String(errors.file)}</p>}
                    </Field>
                  </Column>
                </Row>
                <Row>
                  <Column width={4}>
                    <Radio
                      name="requireSignature"
                      label="Require Signature"
                      toggle
                      checked={values.requireSignature}
                      onChange={(_, { checked }) => setFieldValue('requireSignature', checked)}
                    />
                  </Column>
                  <Column width={4}>
                    <Radio
                      name="requireUserUpload"
                      label="Require User Upload"
                      toggle
                      checked={values.requireUserUpload}
                      onChange={(_, { checked }) => setFieldValue('requireUserUpload', checked)}
                    />
                  </Column>
                  <Column width={8} />
                </Row>
                <Row>
                  <Column width={16}>
                    <p className={style.form_headers}>Distribution</p>
                    <p>Select positions below that will see this document during onboarding</p>
                    <PositionGroup
                      handlePositionClick={setFieldValue}
                      selectedPositions={values.positions}
                    />
                    {showPositionsError && (
                      <p className="error_class">{String(errors.positions)}</p>
                    )}
                  </Column>
                </Row>
                <Row>
                  <Column width={16}>
                    <ButtonBar
                      onSave={handleSubmit}
                      link="/onboarding/forms/templates"
                      disabled={isSubmitting}
                      saveText="Publish"
                    />
                  </Column>
                </Row>
              </Grid>
            );
          }}
        </Formik>
      </Segment>

      <CreateOnboardingFormModal open={shouldOpenFinishModal} handleClose={closeModal} />
    </>
  );
});
